import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Workshops.css";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import CustomAlert from "../../components/Alerts/CustomAlert";
import Modal from "../../components/Dialog/Modal";
import Button from "../../components/Button/Button";
import Chips from "../../components/Chip/Chips";
import addIcon from "../../assets/Icons/plusIcon.svg";
import editIcon from "../../assets/Icons/editIcon.svg";
import { addAlert, removeAlert } from "../../Utils/helper";
import {
  WORKSHOP_TYPE_OPTIONS,
  deleteWorkshop,
  duplicateWorkshop,
  listWorkshops,
  listWorkshopTypes,
  toggleWorkshopActive,
} from "../../Utils/workshopApi";

const WorkshopsList = () => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [workshopTypes, setWorkshopTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [filterWorkshopType, setFilterWorkshopType] = useState("");
  const [filterTypeId, setFilterTypeId] = useState("");
  const [filterActive, setFilterActive] = useState("");

  const [pendingDelete, setPendingDelete] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const handleAlert = (title, message, type) => {
    addAlert(title, message, type, true, setAlerts);
  };
  const handleRemoveAlert = (id) => removeAlert(id, setAlerts);

  const params = useMemo(() => {
    const p = {};
    if (search) p.search = search;
    if (filterWorkshopType) p.workshop_type = filterWorkshopType;
    if (filterTypeId) p.type = filterTypeId;
    if (filterActive) p.is_active = filterActive;
    return p;
  }, [search, filterWorkshopType, filterTypeId, filterActive]);

  const load = async () => {
    setIsLoading(true);
    try {
      const { items } = await listWorkshops(params);
      setWorkshops(items);
    } catch (err) {
      console.error("Failed to load workshops", err);
      handleAlert("Error", "Could not load workshops.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    listWorkshopTypes()
      .then((items) => setWorkshopTypes(items || []))
      .catch(() => setWorkshopTypes([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onToggleActive = async (workshop) => {
    try {
      await toggleWorkshopActive(workshop.id);
      handleAlert(
        "Updated",
        `Workshop ${workshop.is_active ? "unpublished" : "published"}.`,
        "success"
      );
      load();
    } catch (err) {
      handleAlert("Error", "Failed to update workshop status.", "error");
    }
  };

  const onDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteWorkshop(pendingDelete.id);
      handleAlert("Deleted", `"${pendingDelete.title}" was deleted.`, "success");
      setPendingDelete(null);
      load();
    } catch (err) {
      handleAlert("Error", "Failed to delete workshop.", "error");
    }
  };

  const onDuplicate = async (workshop) => {
    try {
      const created = await duplicateWorkshop(workshop.id);
      handleAlert("Duplicated", "Created a draft copy.", "success");
      if (created?.id) navigate(`/workshops/${created.id}/edit`);
      else load();
    } catch (err) {
      handleAlert("Error", "Failed to duplicate workshop.", "error");
    }
  };

  const formatPrice = (w) => {
    const inr = w.selling_price ?? w.mrp;
    const usd = w.selling_price_usd ?? w.mrp_usd;
    const parts = [];
    if (inr) parts.push(`₹${Number(inr).toLocaleString("en-IN")}`);
    if (usd) parts.push(`$${Number(usd).toLocaleString("en-US")}`);
    return parts.length ? parts.join(" / ") : "—";
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className="main-container">
        <div className="ws-list-container">
          <div className="ws-list-header">
            <div className="ws-list-header-left">
              <h2>Workshops</h2>
              <span className="ws-list-count">{workshops.length} total</span>
            </div>
            <div className="ws-list-header-right">
              <Chips
                icon={addIcon}
                text="Create Workshop"
                onClick={() => navigate("/workshops/new")}
              />
            </div>
          </div>

          <div className="ws-filters">
            <input
              className="ws-search"
              type="text"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="ws-select"
              value={filterWorkshopType}
              onChange={(e) => setFilterWorkshopType(e.target.value)}
            >
              <option value="">All categories</option>
              {WORKSHOP_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="ws-select"
              value={filterTypeId}
              onChange={(e) => setFilterTypeId(e.target.value)}
            >
              <option value="">All formats</option>
              {workshopTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
            <select
              className="ws-select"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="">Any status</option>
              <option value="true">Published</option>
              <option value="false">Draft</option>
            </select>
            {(search ||
              filterWorkshopType ||
              filterTypeId ||
              filterActive) && (
              <button
                className="ws-link-btn"
                onClick={() => {
                  setSearch("");
                  setFilterWorkshopType("");
                  setFilterTypeId("");
                  setFilterActive("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {workshops.length === 0 && !isLoading && (
            <div className="ws-empty">
              <h3>No workshops yet</h3>
              <p>
                Create your first workshop to start enrolling parents and
                experts.
              </p>
              <Button
                text="Create Workshop"
                onClick={() => navigate("/workshops/new")}
              />
            </div>
          )}

          <div className="ws-grid">
            {workshops.map((w) => (
              <div className="ws-card" key={w.id}>
                <div className="ws-card-banner">
                  {w.file ? (
                    <img src={w.file} alt={w.title} />
                  ) : (
                    <div className="ws-card-banner-placeholder">
                      {w.title?.[0]?.toUpperCase() || "W"}
                    </div>
                  )}
                  <span
                    className={`ws-status ${
                      w.is_active ? "published" : "draft"
                    }`}
                  >
                    {w.is_active ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="ws-card-body">
                  <h3 className="ws-card-title" title={w.title}>
                    {w.title}
                  </h3>
                  <div className="ws-card-meta">
                    <span>{w.workshop_type_display || "—"}</span>
                    {w.type?.title && <span>· {w.type.title}</span>}
                  </div>
                  <div className="ws-card-stats">
                    <div>
                      <strong>{w.section_count}</strong>
                      <span>sections</span>
                    </div>
                    <div>
                      <strong>{w.expert_count}</strong>
                      <span>experts</span>
                    </div>
                    <div>
                      <strong>{w.enrolments || 0}</strong>
                      <span>enrolled</span>
                    </div>
                  </div>
                  <div className="ws-card-price">{formatPrice(w)}</div>
                </div>
                <div className="ws-card-actions">
                  <button
                    className="ws-action-btn"
                    onClick={() => navigate(`/workshops/${w.id}`)}
                  >
                    View
                  </button>
                  <button
                    className="ws-action-btn primary"
                    onClick={() => navigate(`/workshops/${w.id}/edit`)}
                  >
                    <img src={editIcon} alt="" /> Edit
                  </button>
                  <button
                    className="ws-action-btn"
                    onClick={() => onToggleActive(w)}
                  >
                    {w.is_active ? "Unpublish" : "Publish"}
                  </button>
                  <button
                    className="ws-action-btn"
                    onClick={() => onDuplicate(w)}
                  >
                    Duplicate
                  </button>
                  <button
                    className="ws-action-btn danger"
                    onClick={() => setPendingDelete(w)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {pendingDelete && (
          <Modal title="Delete workshop?" onClose={() => setPendingDelete(null)}>
            <p>
              This will permanently delete{" "}
              <strong>{pendingDelete.title}</strong> along with its sections,
              chapters, videos and FAQs. This cannot be undone.
            </p>
            <div className="confirmation-buttons">
              <Button
                text="Cancel"
                backgroundColor="#ccc"
                onClick={() => setPendingDelete(null)}
              />
              <Button
                text="Delete"
                backgroundColor="#D11A2A"
                onClick={onDelete}
              />
            </div>
          </Modal>
        )}

        {alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            title={alert.title}
            message={alert.message}
            type={alert.type}
            autoClose={alert.autoClose}
            onClose={() => handleRemoveAlert(alert.id)}
          />
        ))}
      </div>
    </>
  );
};

export default WorkshopsList;
